import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { DefaultDropdown } from "../../../../_helpers/components/Forms/Dropdown";
import { InputForm } from "../../../../_helpers/components/Forms/InputLabel";
import { Button } from "../../../../_helpers/components/Button";
import { Loader } from "../../../../_helpers/components/Forms/Loader";
import {
    createPlanningHourLimitRule,
    destroyPlanningHourLimitRule,
    getPlanningHourLimitRules,
    RULE_PRESETS,
    updatePlanningHourLimitRule,
} from "../../../../_api/PlanningHourLimitRule";

const PERIOD_OPTIONS = [
    { value: "day", label: "Journée" },
    { value: "week", label: "Semaine" },
    { value: "month", label: "Mois" },
    { value: "rolling_weeks", label: "X semaines (glissantes)" },
];

const AGGREGATION_OPTIONS = [
    { value: "total", label: "Total sur la période" },
    { value: "average", label: "Moyenne hebdomadaire" },
];

const ENFORCEMENT_OPTIONS = [
    { value: "block", label: "Blocage" },
    { value: "alert", label: "Alerte" },
];

const LIMIT_UNIT_OPTIONS = [
    { value: "hours", label: "Heures" },
    { value: "days", label: "Jours" },
];

const formatThreshold = (value, unit) => {
    const n = Number(value);
    if (Number.isNaN(n)) return "";
    const formatted = Number.isInteger(n) ? String(n) : String(n);
    return unit === "days" ? `${formatted} j max` : `${formatted} h max`;
};

const periodLabel = (value) =>
    PERIOD_OPTIONS.find((o) => o.value === value)?.label ?? value;

const ruleSummary = (rule) => {
    const htCount = (rule.hour_type_ids || []).length;
    const unit = rule.limit_unit || "hours";
    const parts = [
        htCount > 0 && `${htCount} type(s) d'heure`,
        periodLabel(rule.period_type),
        rule.period_type === "rolling_weeks" && rule.period_weeks
            ? `${rule.period_weeks} sem.`
            : null,
        rule.aggregation === "average" ? "moyenne" : "total",
        formatThreshold(rule.max_hours, unit),
        rule.enforcement === "block" ? "blocage" : "alerte",
        rule.enabled === false ? "désactivée" : null,
    ].filter(Boolean);
    return parts.join(" · ");
};

const emptyRule = (hourTypes) => ({
    id: null,
    name: "Nouvelle règle",
    enabled: true,
    hour_type_ids: hourTypes.length > 0 ? [hourTypes[0].id] : [],
    period_type: "week",
    period_weeks: null,
    aggregation: "total",
    limit_unit: "hours",
    max_hours: 35,
    enforcement: "block",
    sort_order: 0,
    _isNew: true,
});

export const PlanningHourLimitRulesEditor = ({ hourTypes, canManage }) => {
    const [rules, setRules] = useState([]);
    const [expanded, setExpanded] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [presetsOpen, setPresetsOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await getPlanningHourLimitRules();
            const list = resp.data?.data ?? [];
            setRules(list);
        } catch {
            toast.error("Impossible de charger les règles de limites d'heures.");
            setRules([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const updateRule = (index, field, value) => {
        setRules((prev) => {
            const next = [...prev];
            const updated = { ...next[index], [field]: value };
            if (field === "period_type" && value !== "rolling_weeks") {
                updated.period_weeks = null;
                updated.aggregation = "total";
            }
            if (field === "period_type" && value === "rolling_weeks" && !updated.period_weeks) {
                updated.period_weeks = 12;
                updated.aggregation = "average";
            }
            next[index] = updated;
            return next;
        });
    };

    const toggleExpanded = (index) => {
        setExpanded((prev) => {
            const next = [...prev];
            next[index] = !next[index];
            return next;
        });
    };

    const persistRule = async (index) => {
        const rule = rules[index];
        if (!rule.hour_type_ids?.length) {
            toast.error("Sélectionnez au moins un type d'heure.");
            return;
        }

        const payload = {
            name: rule.name,
            enabled: rule.enabled !== false,
            hour_type_ids: rule.hour_type_ids,
            period_type: rule.period_type,
            period_weeks: rule.period_type === "rolling_weeks" ? rule.period_weeks : null,
            aggregation:
                rule.period_type === "rolling_weeks"
                    ? rule.aggregation || "average"
                    : "total",
            limit_unit: rule.limit_unit || "hours",
            max_hours: parseFloat(rule.max_hours),
            enforcement: rule.enforcement,
            sort_order: rule.sort_order ?? index,
        };

        setSavingId(rule.id ?? `new-${index}`);
        try {
            if (rule.id) {
                const resp = await updatePlanningHourLimitRule(rule.id, payload);
                toast.success("Règle mise à jour.");
                setRules((prev) => {
                    const next = [...prev];
                    next[index] = resp.data?.data ?? rule;
                    return next;
                });
            } else {
                const resp = await createPlanningHourLimitRule(payload);
                toast.success("Règle créée.");
                setRules((prev) => {
                    const next = [...prev];
                    next[index] = resp.data?.data;
                    return next;
                });
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.message || "Erreur lors de l'enregistrement."
            );
        } finally {
            setSavingId(null);
        }
    };

    const removeRule = async (index) => {
        const rule = rules[index];
        if (rule.id) {
            try {
                await destroyPlanningHourLimitRule(rule.id);
                toast.success("Règle supprimée.");
            } catch {
                toast.error("Impossible de supprimer la règle.");
                return;
            }
        }
        setRules((prev) => prev.filter((_, i) => i !== index));
        setExpanded((prev) => prev.filter((_, i) => i !== index));
    };

    const addRule = () => {
        setRules((prev) => [...prev, emptyRule(hourTypes)]);
        setExpanded((prev) => [...prev.map(() => false), true]);
    };

    const addPreset = (preset) => {
        setRules((prev) => [
            ...prev,
            {
                ...emptyRule(hourTypes),
                ...preset,
                hour_type_ids: (() => {
                    const filter = preset.hour_type_filter;
                    const pool =
                        filter === "absence"
                            ? hourTypes.filter((ht) => ht.is_absence)
                            : filter === "work"
                              ? hourTypes.filter((ht) => ht.is_work)
                              : hourTypes;
                    if (pool.length > 0) {
                        return [pool[0].id];
                    }
                    return hourTypes.length > 0 ? [hourTypes[0].id] : [];
                })(),
                _isNew: true,
            },
        ]);
        setExpanded((prev) => [...prev.map(() => false), true]);
    };

    if (loading) {
        return <Loader text="Chargement des règles..." />;
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-8 w-full">
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">
                            Règles de limites (heures / jours)
                        </h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Contrôles sur le planning prévisionnel.
                        </p>
                    </div>
                    {canManage && (
                        <div className="flex flex-wrap gap-2">
                            <div className="relative">
                                <Button
                                    type="empty"
                                    name="Modèles ▾"
                                    action={() => setPresetsOpen((v) => !v)}
                                />
                                {presetsOpen && (
                                    <div className="absolute right-0 z-10 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                                        {RULE_PRESETS.map((preset) => (
                                            <button
                                                key={preset.name}
                                                type="button"
                                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                                onClick={() => {
                                                    addPreset(preset);
                                                    setPresetsOpen(false);
                                                }}
                                            >
                                                {preset.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Button type="empty" name="+ Règle" action={addRule} />
                        </div>
                    )}
                </div>
            </div>

            <div className="p-6">
                {rules.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">
                        Aucune règle configurée.
                        {canManage && " Cliquez sur « + Règle » ou choisissez un modèle."}
                    </p>
                ) : (
                    <div className="space-y-2">
                        {rules.map((rule, index) => {
                            const isOpen = expanded[index];
                            const isSaving =
                                savingId === `new-${index}` ||
                                (rule.id != null && savingId === rule.id);

                            return (
                                <div
                                    key={rule.id ?? `new-${index}`}
                                    className="rounded-lg border border-gray-200 overflow-hidden bg-white"
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggleExpanded(index)}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                                    >
                                        <svg
                                            className={[
                                                "w-4 h-4 shrink-0 text-gray-400 transition-transform",
                                                isOpen ? "rotate-90" : "",
                                            ].join(" ")}
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 5l7 7-7 7"
                                            />
                                        </svg>
                                        <div className="flex-1 min-w-0">
                                            <span className="font-medium text-gray-900 text-sm">
                                                {rule.name || `Règle ${index + 1}`}
                                            </span>
                                            {!isOpen && (
                                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                                    {ruleSummary(rule)}
                                                </p>
                                            )}
                                        </div>
                                        {canManage && (
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeRule(index);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.stopPropagation();
                                                        removeRule(index);
                                                    }
                                                }}
                                                className="text-xs text-red-600 hover:text-red-800 px-2 py-1 shrink-0"
                                            >
                                                Suppr.
                                            </span>
                                        )}
                                    </button>

                                    {isOpen && (
                                        <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-3">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <InputForm
                                                    name="Nom"
                                                    value={rule.name}
                                                    readOnly={!canManage}
                                                    set={(val) =>
                                                        updateRule(index, "name", val)
                                                    }
                                                />
                                                <DefaultDropdown
                                                    name="Actif"
                                                    value={rule.enabled !== false ? "1" : "0"}
                                                    readOnly={!canManage}
                                                    options={[
                                                        { value: "1", label: "Oui" },
                                                        { value: "0", label: "Non" },
                                                    ]}
                                                    onChange={(e) =>
                                                        updateRule(
                                                            index,
                                                            "enabled",
                                                            e.target.value === "1"
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wide text-app-primary mb-1.5">
                                                    Types d'heures
                                                </label>
                                                <select
                                                    multiple
                                                    disabled={!canManage}
                                                    size={Math.min(
                                                        5,
                                                        Math.max(3, hourTypes.length)
                                                    )}
                                                    className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-gray-50 focus:bg-white focus:outline-none focus:border-gray-400 disabled:opacity-60"
                                                    value={(rule.hour_type_ids || []).map(String)}
                                                    onChange={(e) => {
                                                        const selected = Array.from(
                                                            e.target.selectedOptions
                                                        ).map((opt) =>
                                                            parseInt(opt.value, 10)
                                                        );
                                                        updateRule(
                                                            index,
                                                            "hour_type_ids",
                                                            selected
                                                        );
                                                    }}
                                                >
                                                    {hourTypes.map((ht) => (
                                                        <option key={ht.id} value={ht.id}>
                                                            {ht.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                                <DefaultDropdown
                                                    name="Période"
                                                    value={rule.period_type}
                                                    readOnly={!canManage}
                                                    options={PERIOD_OPTIONS}
                                                    onChange={(e) =>
                                                        updateRule(
                                                            index,
                                                            "period_type",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                {rule.period_type === "rolling_weeks" && (
                                                    <>
                                                        <InputForm
                                                            name="Nombre de semaines"
                                                            type="number"
                                                            value={rule.period_weeks ?? 12}
                                                            readOnly={!canManage}
                                                            set={(val) =>
                                                                updateRule(
                                                                    index,
                                                                    "period_weeks",
                                                                    parseInt(val, 10) || 1
                                                                )
                                                            }
                                                        />
                                                        <DefaultDropdown
                                                            name="Agrégation"
                                                            value={
                                                                rule.aggregation || "average"
                                                            }
                                                            readOnly={!canManage}
                                                            options={AGGREGATION_OPTIONS}
                                                            onChange={(e) =>
                                                                updateRule(
                                                                    index,
                                                                    "aggregation",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </>
                                                )}
                                                <DefaultDropdown
                                                    name="Unité du seuil"
                                                    value={rule.limit_unit || "hours"}
                                                    readOnly={!canManage}
                                                    options={LIMIT_UNIT_OPTIONS}
                                                    onChange={(e) =>
                                                        updateRule(
                                                            index,
                                                            "limit_unit",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                <InputForm
                                                    name={
                                                        (rule.limit_unit || "hours") === "days"
                                                            ? "Seuil max (jours)"
                                                            : "Seuil max (heures)"
                                                    }
                                                    type="number"
                                                    value={rule.max_hours}
                                                    readOnly={!canManage}
                                                    set={(val) =>
                                                        updateRule(
                                                            index,
                                                            "max_hours",
                                                            val
                                                        )
                                                    }
                                                />
                                                <DefaultDropdown
                                                    name="Action"
                                                    value={rule.enforcement}
                                                    readOnly={!canManage}
                                                    options={ENFORCEMENT_OPTIONS}
                                                    onChange={(e) =>
                                                        updateRule(
                                                            index,
                                                            "enforcement",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>

                                            {canManage && (
                                                <div className="flex justify-end pt-1">
                                                    <Button
                                                        type="primary"
                                                        name="Enregistrer"
                                                        action={() => persistRule(index)}
                                                        loading={isSaving}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlanningHourLimitRulesEditor;
